/**
 * Cortex Thinking Animation Component
 *
 * Provides engaging visual feedback while Cortex processes requests.
 * Shows real-time status updates (Connecting, Researching, Preparing response)
 * Similar to ChatGPT/Copilot's thinking indicators.
 *
 * When parallel sub-agents are dispatched, renders a Cursor/Grok-style
 * concurrent agent visualization via SubAgentVisualization.
 *
 * Uses Framer Motion for smooth animations like SystemBootSequence.
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Loader2, Brain, Search, Zap, Sparkles, CheckCircle2, Network } from 'lucide-react';
import { cn } from '@flowstate/utils';
import type { OrchestratorMeta } from '../types/chat';
import { agentBranding } from '../config/agentBranding';
import { SubAgentVisualization } from './SubAgentVisualization';
import { ToolActivityTimeline } from './ToolActivityTimeline';

interface CortexThinkingAnimationProps {
  orchestratorMeta?: OrchestratorMeta;
  statusMessage?: string;
  compact?: boolean;
}

interface ThinkingStep {
  label: string;
  icon: React.ReactNode;
  status: 'pending' | 'active' | 'complete';
}

const stepIcons = [<Zap className="h-4 w-4" key="zap" />, <Network className="h-4 w-4" key="network" />, <Brain className="h-4 w-4" key="brain" />, <Search className="h-4 w-4" key="search" />, <Sparkles className="h-4 w-4" key="sparkles" />];
const defaultSteps = agentBranding.thinkingSteps.map((label, idx) => ({
  label,
  icon: stepIcons[idx] ?? <Sparkles className="h-4 w-4" key="s" />,
}));

/** Format milliseconds into human-readable elapsed time */
const formatElapsed = (ms: number): string => {
  if (ms < 1000) return '0.0s';
  return `${(ms / 1000).toFixed(1)}s`;
};

/** Tier label map for model badge */
const TIER_LABELS: Record<string, string> = {
  fast: '⚡ Fast',
  balanced: '⚖️ Balanced',
  reasoning: '🧠 Reasoning',
  code: '💻 Code',
};

export const CortexThinkingAnimation: React.FC<CortexThinkingAnimationProps> = ({
  orchestratorMeta,
  statusMessage,
  compact = false,
}) => {
  const [_activeStepIndex, setActiveStepIndex] = useState(0);
  const [steps, setSteps] = useState<ThinkingStep[]>(
    defaultSteps.map((step, idx) => ({
      ...step,
      status: idx === 0 ? 'active' : 'pending',
    }))
  );

  // ── Live elapsed timer ─────────────────────────────────────────
  const [elapsedMs, setElapsedMs] = useState(0);
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!orchestratorMeta?.isThinking) return;
    startTimeRef.current = Date.now();
    setElapsedMs(0);
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 100);
    return () => clearInterval(interval);
  }, [orchestratorMeta?.isThinking]);

  // Auto-progress through branded default steps only when brain has not sent real steps
  useEffect(() => {
    if (!orchestratorMeta?.isThinking) {return;}
    if (orchestratorMeta?.thinkingSteps && orchestratorMeta.thinkingSteps.length > 0) {
      return;
    }

    const interval = setInterval(() => {
      setActiveStepIndex((prev) => {
        const nextIndex = prev < defaultSteps.length - 1 ? prev + 1 : prev;

        setSteps(defaultSteps.map((step, idx) => ({
          ...step,
          status: idx < nextIndex ? 'complete' : idx === nextIndex ? 'active' : 'pending',
        })));

        return nextIndex;
      });
    }, 1200);

    return () => clearInterval(interval);
  }, [orchestratorMeta?.isThinking, orchestratorMeta?.thinkingSteps]);

  // Prefer real thinking steps from brain status events
  useEffect(() => {
    if (orchestratorMeta?.thinkingSteps && orchestratorMeta.thinkingSteps.length > 0) {
      const current = orchestratorMeta.currentStep ?? 0;
      const customSteps = orchestratorMeta.thinkingSteps.map((label, idx) => ({
        label,
        icon: stepIcons[idx] ?? <Sparkles className="h-4 w-4" />,
        status: (idx < current
          ? 'complete'
          : idx === current
            ? 'active'
            : 'pending') as ThinkingStep['status'],
      }));
      setSteps(customSteps);
      setActiveStepIndex(current);
    }
  }, [orchestratorMeta?.thinkingSteps, orchestratorMeta?.currentStep]);

  if (!orchestratorMeta?.isThinking && !statusMessage) {return null;}

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="flex items-center gap-2 text-sm text-muted-foreground italic"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="flex-1">{statusMessage || steps.find(s => s.status === 'active')?.label || 'Thinking...'}</span>
        {orchestratorMeta?.isThinking && (
          <span className="text-xs font-mono tabular-nums opacity-70">{formatElapsed(elapsedMs)}</span>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <div className="w-full rounded-2xl p-4 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 border border-primary/10">
        {/* ── Header: Model Badge + Live Timer ──────────────────── */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs">
            {orchestratorMeta?.modelName ? (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2"
              >
                <div className="px-2 py-1 rounded-md bg-primary/10 text-primary font-medium">
                  {TIER_LABELS[orchestratorMeta.modelTier ?? ''] ?? '🤖 AI'}
                </div>
                <span className="text-muted-foreground opacity-70">{orchestratorMeta.modelName}</span>
              </motion.div>
            ) : (
              <div className="px-2 py-1 rounded-md bg-primary/10 text-primary font-medium">
                Cortex
              </div>
            )}
          </div>
          {/* Live elapsed timer with pulse dot */}
          {orchestratorMeta?.isThinking && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-1.5 text-xs font-mono tabular-nums text-muted-foreground"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span>{formatElapsed(elapsedMs)}</span>
            </motion.div>
          )}
        </div>

        {/* ── Status Console — real-time brain telemetry ──────── */}
        <AnimatePresence mode="wait">
          {statusMessage && (
            <motion.div
              key={statusMessage}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-2.5 mb-3 px-3 py-2 rounded-lg bg-primary/[0.08] dark:bg-primary/[0.12]"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary flex-shrink-0" />
              <span className="text-sm font-medium text-primary">{statusMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sub-Agent Parallel Visualization (Cursor/Grok-style) */}
        {orchestratorMeta?.subAgentActivity &&
          orchestratorMeta.subAgentActivity.agents.length > 0 && (
            <div className="mb-3">
              <SubAgentVisualization activity={orchestratorMeta.subAgentActivity} />
            </div>
          )}

        {/* Live tool-call trail */}
        {orchestratorMeta?.toolActivity &&
          orchestratorMeta.toolActivity.entries.length > 0 && (
            <div className="mb-3">
              <ToolActivityTimeline activity={orchestratorMeta.toolActivity} />
            </div>
          )}

        {/* Thinking Steps - Only show active/current step (hidden when sub-agents are active) */}
        {!(orchestratorMeta?.subAgentActivity?.isActive) && (
          <AnimatePresence>
            {steps.map((step, index) => {
              // Only render the active step or completed steps that are collapsing
              if (step.status === 'pending') {return null;}

              return (
                <motion.div
                  key={`${step.label}-${index}`}
                  initial={{ height: 'auto', opacity: 1, x: -20 }}
                  animate={{
                    height: step.status === 'complete' ? 0 : 'auto',
                    opacity: step.status === 'complete' ? 0 : 1,
                    x: 0,
                    marginBottom: step.status === 'complete' ? 0 : undefined,
                    paddingTop: step.status === 'complete' ? 0 : undefined,
                    paddingBottom: step.status === 'complete' ? 0 : undefined,
                  }}
                  exit={{ height: 0, opacity: 0, x: 20 }}
                  transition={{
                    duration: step.status === 'complete' ? 0.4 : 0.3,
                    delay: step.status === 'complete' ? 0.3 : index * 0.05,
                    ease: 'easeInOut'
                  }}
                  className="overflow-hidden"
                >
                  <div
                    className={cn(
                      'flex items-center gap-3 p-2.5 rounded-lg transition-all duration-300',
                      step.status === 'complete' && 'text-green-600 dark:text-green-400',
                      step.status === 'active' && 'text-primary bg-primary/10 font-medium',
                    )}
                  >
                    {/* Icon with animation */}
                    <div className="relative">
                      {step.status === 'complete' && (
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 200 }}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </motion.div>
                      )}
                      {step.status === 'active' && (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        >
                          {step.icon}
                        </motion.div>
                      )}
                    </div>

                    {/* Label */}
                    <span className="flex-1 text-sm">{step.label}</span>

                    {/* Status indicator */}
                    {step.status === 'active' && (
                      <motion.div
                        className="flex space-x-1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <motion.div
                          className="w-1.5 h-1.5 rounded-full bg-primary"
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ duration: 1, repeat: Infinity }}
                        />
                        <motion.div
                          className="w-1.5 h-1.5 rounded-full bg-primary"
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                        />
                        <motion.div
                          className="w-1.5 h-1.5 rounded-full bg-primary"
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                        />
                      </motion.div>
                    )}
                    {step.status === 'complete' && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-xs text-green-600 dark:text-green-400"
                      >
                        ✓
                      </motion.span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}

        {/* ── Progress beam — glowing gradient ────────────────── */}
        {orchestratorMeta?.isThinking && (
          <div className="relative h-1 mt-3 overflow-hidden rounded-full bg-muted">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-primary/60 via-accent/60 to-primary/60 cortex-progress-glow"
              animate={{
                x: ['-100%', '100%'],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default CortexThinkingAnimation;
